import { useEffect, useRef, useState } from "react";
import { canTransition, isTerminal, type ReportStatus } from "@oshimap/domain";
import { reportStatusLabel, reportTypeLabel } from "@oshimap/config";
import { relativeDay, hostLabel } from "@oshimap/utils";
import { ep } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { Button, EmptyState, ErrorState, Skeleton } from "../../components/ui";
import "./report.css";
import { wiggle } from "../../lib/motion";
import { prefersReducedMotion } from "../../lib/reduced-motion";
import { queryKeys } from "@oshimap/query";

/**
 * `highlightId` 는 알림에서 넘어온 특정 제보다.
 * 목록만 열어 주고 "찾아보세요" 하면 알림을 누른 뜻이 없다 — 그 줄로 데려간다.
 */
export function ReportsPane({ highlightId }: { highlightId?: string }) {
  const list = useQuery(queryKeys.reports(), (signal) => ep.listReports({ signal }));
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const marked = useRef<string | null>(null);

  useEffect(() => {
    if (!highlightId || marked.current === highlightId) return;
    if (list.state.status !== "success") return;
    const el = document.getElementById(`report-${highlightId}`);
    if (!el) return;
    marked.current = highlightId;
    el.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
    /* 어디로 왔는지 보이게 한 번 짚어 준다. 계속 켜 두면 그냥 다른 색 줄이 된다. */
    wiggle(el);
  }, [highlightId, list.state]);

  if (list.state.status === "loading" || list.state.status === "idle") {
    return <div className="om-me__pad"><Skeleton lines={3} /></div>;
  }
  if (list.state.status === "error") {
    return <div className="om-me__pad">
      <ErrorState title="제보를 불러오지 못했어요" body={list.state.error.message} onRetry={list.reload} />
    </div>;
  }

  const items = list.state.data;
  if (items.length === 0) {
    return <div className="om-me__pad">
      {/* 비었을 때는 이게 이 구역의 제목이다 — h1 다음은 h2 여야 한다. */}
      <EmptyState level={2} title="아직 보낸 제보가 없어요"
        body="바뀐 정보를 알려주시면 검수 후 반영됩니다." />
    </div>;
  }

  const act = async (id: string, run: () => Promise<unknown>) => {
    setBusy(id); setError(null);
    try { await run(); list.reload(); }
    catch (e) { setError(e instanceof Error ? e.message : "처리하지 못했어요."); }
    finally { setBusy(null); }
  };

  return (
    <div className="om-me__pad">
      <h2 className="om-section__title" style={{ padding: 0 }}>
        내 제보 <span className="num om-count">{items.length}</span>
      </h2>
      {error ? <p className="om-note om-note--err" role="alert">{error}</p> : null}

      <ul className="om-reports">
        {items.map((r) => {
          /* 이름표는 config 한 곳에서 온다 — Native 도 같은 표를 읽는다. */
          const s = reportStatusLabel(r.status);
          const status = r.status as ReportStatus;
          /* 상태 기계는 도메인이 안다. 화면이 규칙을 다시 적지 않는다 (P6). */
          const canWithdraw = canTransition(status, "WITHDRAWN");
          const canResubmit = canTransition(status, "SUBMITTED");
          return (
            <li key={r.id} id={`report-${r.id}`}
              className={`om-report${highlightId === r.id ? " om-report--marked" : ""}`}>
              <div className="om-report__top">
                <span className={`om-report__st om-report__st--${s.tone}`}>{s.label}</span>
                <span className="om-report__type">{reportTypeLabel(r.reportType)}</span>
                <time className="num om-report__at" dateTime={r.createdAt}>{relativeDay(r.createdAt)}</time>
              </div>

              <b className="om-report__title">{r.title}</b>
              <p className="om-report__body">{s.body}</p>

              {r.reviewNote ? (
                <p className="om-report__note"><b>검수 메모</b> {r.reviewNote}</p>
              ) : null}

              {r.sourceLinks.length > 0 ? (
                <p className="om-report__src">
                  출처 {r.sourceLinks.map((l) => (
                    <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer nofollow">
                      {hostLabel(l.url)}
                    </a>
                  ))}
                </p>
              ) : null}

              {isTerminal(status) ? null : (
                <div className="om-report__acts">
                  {canResubmit ? (
                    <Button variant="secondary" size="sm" disabled={busy === r.id}
                      onClick={() => void act(r.id, () => ep.submitReport(r.id))}>
                      {r.status === "DRAFT" ? "제출하기" : "다시 제출"}
                    </Button>
                  ) : null}
                  {canWithdraw ? (
                    <Button variant="ghost" size="sm" disabled={busy === r.id}
                      onClick={() => void act(r.id, () => ep.withdrawReport(r.id))}>철회</Button>
                  ) : null}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
